import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import { Loader2, Save, UserRound, Phone, MapPin, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { capitalizeFirst } from "@/lib/appresse";

const SECTIONS = [
  {
    id: "identity", label: "Identité", icon: UserRound,
    fields: [
      { k: "civility", label: "Civilité", type: "select", options: ["", "M.", "Mme", "Autre"] },
      { k: "first_name", label: "Prénom" },
      { k: "last_name", label: "Nom" },
      { k: "birthdate", label: "Date de naissance", type: "date" },
      { k: "language", label: "Langue", type: "select", options: ["fr", "en", "es", "de", "it"] },
      { k: "country_residence", label: "Pays de résidence" },
      { k: "account_type", label: "Type de compte", type: "select", options: ["particulier", "professionnel"] },
    ],
  },
  {
    id: "contact", label: "Coordonnées", icon: Phone,
    fields: [
      { k: "email_secondary", label: "E-mail secondaire", type: "email" },
      { k: "phone_mobile", label: "Téléphone portable", type: "tel" },
      { k: "phone_secondary", label: "Téléphone secondaire", type: "tel" },
      { k: "contact_preference", label: "Préférence de contact", type: "select", options: ["", "email", "sms", "push", "courrier"] },
      { k: "consent_email", label: "Consentement e-mail", type: "switch" },
      { k: "consent_sms", label: "Consentement SMS", type: "switch" },
      { k: "consent_push", label: "Consentement notifications push", type: "switch" },
    ],
  },
  {
    id: "address", label: "Adresse postale", icon: MapPin,
    fields: [
      { k: "addr_street_no", label: "N° de voie" },
      { k: "addr_street", label: "Nom de la voie" },
      { k: "addr_complement", label: "Complément d'adresse" },
      { k: "addr_building", label: "Bâtiment" },
      { k: "addr_floor", label: "Étage" },
      { k: "addr_apartment", label: "Appartement" },
      { k: "addr_residence", label: "Résidence" },
      { k: "addr_locality", label: "Lieu-dit" },
      { k: "addr_postal_code", label: "Code postal" },
      { k: "addr_city", label: "Ville" },
      { k: "addr_region", label: "Région / Département" },
      { k: "addr_country", label: "Pays" },
    ],
  },
  {
    id: "company", label: "Informations professionnelles", icon: Building2,
    fields: [
      { k: "company", label: "Société" },
      { k: "legal_name", label: "Raison sociale" },
      { k: "job_title", label: "Fonction" },
      { k: "siren", label: "SIREN / SIRET" },
      { k: "vat_number", label: "N° TVA intracommunautaire" },
      { k: "billing_address", label: "Adresse de facturation" },
      { k: "billing_different", label: "Adresse de facturation différente", type: "switch" },
      { k: "billing_info", label: "Informations de facturation" },
    ],
  },
];

export const ProfileForm = ({ initial, onSave, prefix = "profile" }) => {
  const [form, setForm] = useState(() => {
    const o = {};
    SECTIONS.forEach((s) => s.fields.forEach((f) => { o[f.k] = initial?.[f.k] ?? (f.type === "switch" ? false : ""); }));
    o.name = initial?.name ?? "";
    return o;
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(form).forEach(([k, v]) => { clean[k] = v === "—" ? "" : v; });
      await onSave(clean);
      toast.success("Coordonnées enregistrées");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const id = `${prefix}-${f.k}`;
    if (f.type === "switch") {
      return (
        <div key={f.k} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
          <Label htmlFor={id} className="text-sm">{f.label}</Label>
          <Switch id={id} checked={!!form[f.k]} onCheckedChange={(v) => set(f.k, v)} data-testid={`${id}-switch`} />
        </div>
      );
    }
    if (f.type === "select") {
      return (
        <div key={f.k} className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">{f.label}</Label>
          <Select value={form[f.k] || ""} onValueChange={(v) => set(f.k, v)}>
            <SelectTrigger data-testid={`${id}-select`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {f.options.map((o) => <SelectItem key={o || "none"} value={o || "—"}>{o || "—"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }
    return (
      <div key={f.k} className="space-y-1.5">
        <Label htmlFor={id} className="text-xs text-muted-foreground">{f.label}</Label>
        <Input id={id} type={f.type || "text"} value={form[f.k] || ""}
          onChange={(e) => set(f.k, f.type === "email" ? e.target.value.toLowerCase() : (f.type ? e.target.value : capitalizeFirst(e.target.value)))}
          autoCapitalize={f.type === "email" ? "none" : undefined}
          data-testid={`${id}-input`} />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${prefix}-name`} className="text-xs text-muted-foreground">Nom affiché</Label>
        <Input id={`${prefix}-name`} value={form.name || ""} onChange={(e) => set("name", e.target.value)}
          data-testid={`${prefix}-name-input`} />
      </div>
      <Accordion type="multiple" defaultValue={["identity"]} className="w-full">
        {SECTIONS.map((s) => (
          <AccordionItem key={s.id} value={s.id}>
            <AccordionTrigger className="text-sm font-semibold" data-testid={`${prefix}-section-${s.id}`}>
              <span className="flex items-center gap-2"><s.icon className="w-4 h-4 text-primary" /> {s.label}</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid sm:grid-cols-2 gap-3 pt-1">
                {s.fields.map(renderField)}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <Button onClick={save} disabled={saving} className="w-full gap-2" data-testid={`${prefix}-save-button`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Enregistrer les coordonnées
      </Button>
    </div>
  );
};
